import { defineStore, storeToRefs } from 'pinia';
import { useStoresStore } from './stores';
import { useUserStore } from './user';
import { useUiStore } from './ui';
import { useCartStore } from './cart';
import { useAddressStore } from './address';
import { useBrandStore } from './brand';
import { Address } from '@/types/address';
import {
  SORTED_DELIVERY_IDS,
  DELIVERY_ID,
  GET_IN_STORE_ID,
  STORE_EXPRESS,
  DELIVERY_EXPRESS,
  STANDARD_DELIVERIES,
  ERP_DELIVERY_METHOD_MAP,
  LATE_DELIVERY_EXPRESS,
  DELIVERY_ID_OBJECT,
  ORDER_TYPES,
  DeliveryObject,
  OrderType,
  GET_IN_STORE_ID_OBJECT,
} from '~/config/delivery-config';
import { PERMISSION_DENIES } from '~/config/permission-config.js';
import { validateOnlyWhitespace } from '@/utils/InputValidator';
import { CustomerDeliveryDays, DeliveryForm, FormField, FormFieldValue } from '~/types/delivery';
import { StockInput } from '~/types/stock';

export type DeliveryId = typeof DELIVERY_ID | typeof DELIVERY_EXPRESS | typeof GET_IN_STORE_ID | typeof STORE_EXPRESS;

const helpers = {
  deliveryFormHasErrors: (deliveryForm: DeliveryForm): boolean => {
    const someFormFieldHasErrors = Object.values(deliveryForm.formData).some((field) => {
      if (!!field.errorMessage && field.errorMessage.length > 0) return true;
      const minLength = field.minlength || 1;
      if (field.required && (field.value as string).length < minLength) return true;
      if (field.required && validateOnlyWhitespace(field.value as string)) return true;
      if (field.maxlength && field.maxlength < (field.value as string).length) {
        const { $i18n } = useNuxtApp();
        field.errorMessage = $i18n.t('order.maxCharacters', { limit: field.maxlength });
        return true;
      }
      return false;
    });
    return someFormFieldHasErrors;
  },
  shouldDeliveryOptionBeDisplayed: (availableHours: { start: number; end: number }): boolean => {
    if (!availableHours) return true;

    const today = new Date();
    return (
      isCurrentTimeBetweenHours(availableHours.start, availableHours.end) && !isWeekend(today) && !isHoliday(today)
    );
  },
};

const initialSelectedLocations = {
  [DELIVERY_ID]: '',
  [DELIVERY_EXPRESS]: '',
  [GET_IN_STORE_ID]: '',
  [STORE_EXPRESS]: '',
};

export type SelectedLocations = typeof initialSelectedLocations;

const initialZipCodes = {
  [DELIVERY_ID]: '',
  [DELIVERY_EXPRESS]: '',
};

const initialRawLocations = {
  [DELIVERY_ID]: [],
  [DELIVERY_EXPRESS]: [],
  [GET_IN_STORE_ID]: [],
  [STORE_EXPRESS]: [],
};

export const useDeliveryStore = defineStore('delivery', () => {
  const deliveryOptions = ref<DeliveryObject[]>([]);
  const activeDeliveryIdsInOrder = ref<string[]>([]);
  const deliveryForms = ref<DeliveryForm[]>([]);
  const selectedDeliveryId = ref('');
  const selectedLocations = ref<SelectedLocations>({ ...initialSelectedLocations });
  const rawLocations = ref<{ [key: string]: string[] }>({ ...initialRawLocations });
  const deliveryAddresses = ref<{ [key: string]: Address | {} }>({
    [DELIVERY_ID]: {},
    [DELIVERY_EXPRESS]: {},
  });
  const zipCodes = ref<{ [key: string]: string }>({ ...initialZipCodes });
  const sendOrderReceipt = ref(true);
  const deliveryDays = ref<CustomerDeliveryDays | null>(null);

  const setDeliveryOptions = (options: DeliveryObject[]): void => {
    deliveryOptions.value = options;
  };

  const setSelectedDeliveryId = (deliveryId: string): void => {
    selectedDeliveryId.value = deliveryId;
  };

  const setDeliveryAddressForDeliveryId = (deliveryId: string, address: Address | {}): void => {
    const newDeliveryAddresses = { ...deliveryAddresses.value };
    newDeliveryAddresses[deliveryId] = address;
    deliveryAddresses.value = newDeliveryAddresses;
  };

  const setSelectedLocation = (deliveryId: string, locationId: string): void => {
    // Make sure the deliveryId is valid
    if (Object.keys(initialSelectedLocations).includes(deliveryId) === false) return;

    const deliveryOption = getDeliveryOptionById(deliveryId);
    if (locationId && deliveryOption?.useDestination) {
      locationId = 'VIRTUAL';
    }

    const newLocations = { ...selectedLocations.value, [deliveryId]: locationId };
    selectedLocations.value = newLocations;
    localStorage.setItem('selectedLocations', JSON.stringify(newLocations));
  };

  const setNewDeliveryForm = (newDeliveryForm: DeliveryForm): void => {
    const newForms = deliveryForms.value.length ? deliveryForms.value : [];
    newForms.push(newDeliveryForm);
    deliveryForms.value = newForms;
  };

  const setDeliveryFormField = (deliveryFormField: FormField): void => {
    const { name, deliveryId } = deliveryFormField;

    const currentForm = deliveryForms.value.find((form: DeliveryForm) => deliveryId === form.deliveryId);

    if (currentForm) currentForm.formData[name!] = deliveryFormField;
  };

  const setZipCode = (payload: { deliveryId: string; zipCode: string }): void => {
    const { deliveryId, zipCode } = payload;
    if (Object.keys(initialZipCodes).includes(deliveryId)) {
      zipCodes.value[deliveryId] = zipCode;
      localStorage.setItem(`zipCodes`, JSON.stringify(zipCodes.value));
    }
  };

  const setSendOrderReceipt = (value: boolean): void => {
    sendOrderReceipt.value = value;
  };

  const setDeliveryDays = (days: CustomerDeliveryDays | null): void => {
    deliveryDays.value = days;
  };

  const clearState = (): void => {
    activeDeliveryIdsInOrder.value = [];
    deliveryForms.value = [];
    zipCodes.value = { ...initialZipCodes };
    deliveryAddresses.value = {
      [DELIVERY_ID]: {},
      [DELIVERY_EXPRESS]: {},
    };
    sendOrderReceipt.value = true;
    selectedLocations.value = { ...initialSelectedLocations };
    deliveryDays.value = null;
  };

  const clearAddress = (): void => {
    deliveryAddresses.value[DELIVERY_ID] = {};
    deliveryAddresses.value[DELIVERY_EXPRESS] = {};
  };

  const setRawLocations = (deliveryId: string, locations: string[]): void => {
    rawLocations.value[deliveryId] = locations;
  };

  const setPreSelectedLocations = async ({ preferredStore }: { preferredStore: string }): Promise<void> => {
    const cachedLocations = (JSON.parse(localStorage.getItem('selectedLocations') || '""') || {}) as SelectedLocations;
    const { $locationService } = useNuxtApp();
    const { storeIdMap } = useStoresStore();

    for (const deliveryId in selectedLocations.value) {
      let locationId: string | null = cachedLocations?.[deliveryId as DeliveryId] || preferredStore;
      if (deliveryId === 'DBI') {
        locationId = preferredStore;
      }
      const erpDeliveryId = getErpDeliveryMethodById(deliveryId).value;
      const zipCode = JSON.parse(localStorage.getItem(`zipCodes`) || '""')?.[deliveryId] || '';

      const requiresZip = getDeliveryOptionById(deliveryId)?.requiresZipCode;

      if (requiresZip) {
        /* Set location that delivers to the preset zipcode,
        if no zipcode or location yielded set location to null */
        if (zipCode) {
          const query: { zipCode: string; deliveryMethod?: string } = { zipCode };
          if (erpDeliveryId) query.deliveryMethod = erpDeliveryId;
          const { data: locations } = await $locationService.getLocations(query);
          if (locations) {
            const location = locations?.[0];
            locationId = location?.id || null;

            setRawLocations(
              deliveryId,
              locations.map((location: { id: string }) => location.id),
            );
          }
        } else {
          locationId = null;
        }

        setZipCode({ deliveryId, zipCode: locationId ? zipCode : '' });
      }

      let validLocation = true;
      if (!locationId) {
        validLocation = false;
      } else if (erpDeliveryId) {
        // Does the preset store support the delivery method?
        validLocation = storeIdMap[locationId]?.deliveryServices.includes(erpDeliveryId);
      }

      setSelectedLocation(deliveryId, validLocation ? locationId! : '');
    }
  };

  const activateDeliveryIdInOrder = async (deliveryId: string, isActive: boolean): Promise<void> => {
    const { $cartService } = useNuxtApp();
    const { cartId } = useCartStore();
    const { error } = await $cartService.activateInOrder({
      cartId,
      deliveryId,
      isActive,
    });
    if (error) return;
    const newDeliveryIds = new Set(activeDeliveryIdsInOrder.value);
    if (isActive) newDeliveryIds.add(deliveryId);
    else newDeliveryIds.delete(deliveryId);

    activeDeliveryIdsInOrder.value = [...newDeliveryIds];
  };

  const copyDeliveryFormToExistingForms = (deliveryId: string): void => {
    const {
      public: { brand },
    } = useRuntimeConfig();
    const formToCopy = getDeliveryFormById(deliveryId).value;
    const newDeliveryForms = deliveryForms.value.map((form: DeliveryForm) => {
      const newFormData = {
        ...formToCopy!.formData,
        requestedDeliveryDate: { ...form.formData.requestedDeliveryDate },
      };
      return { ...form, formData: newFormData };
    });

    localStorage.setItem(`${brand}-deliveryPageForm`, JSON.stringify(newDeliveryForms));
    deliveryForms.value = newDeliveryForms;
  };

  const activateNewDeliveryIdInOrder = (deliveryId: string): void => {
    if (!getAllActiveDeliveryIdsInOrder.value.includes(deliveryId as DeliveryId)) {
      activateDeliveryIdInOrder(deliveryId, true);
    }
  };

  const setZipCodeFromAddress = async (): Promise<void> => {
    const { fetchAddresses, getPreselectedAddress } = useAddressStore();
    const { addToastMessage } = useUiStore();
    const { $locationService, $i18n } = useNuxtApp();
    const zipCodeDeliveryMethods = getAvailableDeliveryOptions.value.filter(
      (deliveryOption) => deliveryOption.useContextZipCode,
    );
    /**
     * Check if delivery option needs to have zip code.
     */
    if (!zipCodeDeliveryMethods.length) return;

    // Fetch addresses.
    await fetchAddresses();

    if (!getPreselectedAddress) return;

    await Promise.all(
      zipCodeDeliveryMethods.map(async (deliveryMethod: DeliveryObject) => {
        const erpDeliveryId = getErpDeliveryMethodById(deliveryMethod.id).value;
        // Get zipcode from localstorage.
        const zipCodeFromLocalStorage = JSON.parse(localStorage.getItem(`zipCodes`) || '""')?.[erpDeliveryId!] || '';
        /**
         * Check if zipcode from localstorage is the same as preSelectedAddress postalcode.
         * if it is. We should not do any furter requests.
         */
        if (zipCodeFromLocalStorage === getPreselectedAddress.postalCode) return;
        // Get locations based on zipcode and erp id
        const query: { zipCode: string; deliveryMethod?: string } = { zipCode: getPreselectedAddress.postalCode };
        if (erpDeliveryId) query.deliveryMethod = erpDeliveryId;
        const { data: locations } = await $locationService.getLocations(query);
        const location = locations?.[0];
        if (!location) return;

        const formattedZipCode = formatZipCode(getPreselectedAddress.postalCode);
        setSelectedLocation(deliveryMethod.id, location.id);
        setZipCode({ deliveryId: deliveryMethod.id, zipCode: formattedZipCode });
        const text = $i18n.t('address.zip_code_has_been_updated_to', { zipCode: getPreselectedAddress.postalCode });
        addToastMessage({ text });
      }),
    );
  };

  const getStockLocationsList = computed<{ [key: string]: string[] }>(() => {
    const { centralStorageId } = storeToRefs(useStoresStore());
    const stockLocations: { [key: string]: string[] } = {};
    for (const deliveryId in selectedLocations.value) {
      const locationId = selectedLocations.value[deliveryId as DeliveryId];
      stockLocations[deliveryId] = [centralStorageId.value, locationId].filter((id) => id);
    }

    return stockLocations;
  });

  const getStockInput = computed<StockInput>(() => {
    const { centralStorageId } = storeToRefs(useStoresStore());
    const locationIds = Object.values(selectedLocations.value);
    if (centralStorageId) locationIds.push(centralStorageId.value);
    const uniqueLocations = [...new Set(locationIds)];

    const stockInput = {
      locationIds: uniqueLocations.filter(Boolean),
    } as StockInput;

    const useDestinationDelivery = getAvailableDeliveryOptions.value.find((delivery) => delivery.useDestination);

    if (useDestinationDelivery) {
      const postalCode = getZipCode(useDestinationDelivery.id).value;

      if (useDestinationDelivery && postalCode) {
        stockInput.destination = {
          postalCode,
        };
      }
    }
    return stockInput;
  });

  const getCurrentStockLocation = computed<string>(() => {
    return selectedLocations.value[getSelectedDeliveryId.value!];
  });

  const getCurrentStockLocations = computed<string[]>(() => getStockLocationsList.value[getSelectedDeliveryId.value!]);

  const getDeliveryFormById = (deliveryId: string) =>
    computed<DeliveryForm | undefined>(() =>
      deliveryForms.value?.find((form: { deliveryId: string }) => deliveryId === form.deliveryId),
    );

  const getErpDeliveryMethodById = (deliveryId: string) =>
    computed<string | null>(() => {
      // Needs to be accessed explicitly since late and regular express delivery uses same ID
      if (deliveryId === DELIVERY_EXPRESS && lateDeliveryExpressIsActive.value)
        return ERP_DELIVERY_METHOD_MAP[LATE_DELIVERY_EXPRESS];
      return (ERP_DELIVERY_METHOD_MAP as { [key: string]: string | null })[deliveryId];
    });

  const lateDeliveryExpressIsActive = computed<boolean>(() => {
    return !!getAvailableDeliveryOptions.value.find(({ name }) => name === '24-12');
  });

  const getZipCode = (deliveryId: string) => computed<string>(() => zipCodes.value[deliveryId]);

  const getAvailableDeliveryOptions = computed<DeliveryObject[]>(() => {
    const { isPunchout } = useUiStore();
    const { isAllowed } = useUserStore();
    let filteredDeliveryOptions = [...deliveryOptions.value];

    // Filter out options that should not be displayed at the current time
    filteredDeliveryOptions = filteredDeliveryOptions.filter((option) => {
      return helpers.shouldDeliveryOptionBeDisplayed(option.availableBetweenHours!);
    });

    // Filter out options not permitten for the user
    if (!isAllowed(PERMISSION_DENIES.LIMITED_DELIVERIES).value) {
      filteredDeliveryOptions = filteredDeliveryOptions.filter((option) => !STANDARD_DELIVERIES.includes(option.id));
    }

    // Filter out options not permitted for punchout users
    if (isPunchout) {
      const { public: config } = useRuntimeConfig();
      if (config.brand === 'opti') {
        filteredDeliveryOptions = [GET_IN_STORE_ID_OBJECT];
      } else {
        filteredDeliveryOptions = [DELIVERY_ID_OBJECT];
      }
    }

    return filteredDeliveryOptions;
  });

  const getAvailableDeliveryIds = computed<DeliveryId[]>(() => {
    return getAvailableDeliveryOptions.value.map((deliveryOption: DeliveryObject) => deliveryOption.id);
  });

  const getAllActiveDeliveryIdsInOrder = computed<DeliveryId[]>(() =>
    SORTED_DELIVERY_IDS.filter((id) => activeDeliveryIdsInOrder.value.includes(id)),
  );

  const getDeliveryNameFromId = (id: string) =>
    computed<string | undefined>(() => {
      // Needs to be accessed explicitly since late and regular express delivery uses same ID
      if (id === DELIVERY_EXPRESS && lateDeliveryExpressIsActive.value) {
        return LATE_DELIVERY_EXPRESS;
      }
      return getDeliveryOptionById(id)?.name;
    });

  const getDeliveryAddress = computed<Address>((): Address => deliveryAddresses.value[DELIVERY_ID] as Address);
  const getExpressDeliveryAddress = computed<Address>(
    (): Address => deliveryAddresses.value[DELIVERY_EXPRESS] as Address,
  );

  const getOrderTypeForDeliveryId = (id?: string) =>
    computed<OrderType>(() => {
      return getDeliveryOptionById(id || getSelectedDeliveryId.value)?.orderType || ORDER_TYPES.STANDARD;
    });

  const getDeliveryOptionById = (id?: string): DeliveryObject | undefined => {
    const { brandConfig } = useBrandStore();
    return brandConfig.deliveryOptions.find((value) => value.id === id);
  };

  const getSelectedDeliveryId = computed<DeliveryId | undefined>(() => {
    const id = getAvailableDeliveryIds.value.find((id) => id === selectedDeliveryId.value);
    return id;
  });

  const setActiveDeliveryIdsInOrder = (deliveryIds: string[]): void => {
    activeDeliveryIdsInOrder.value = deliveryIds;
  };

  const getDeliveryFormsRequestData = computed<{ [key: string]: FormFieldValue }[]>(() => {
    return deliveryForms.value.map((form: DeliveryForm) => {
      const order: { [key: string]: FormFieldValue } = {};
      Object.keys(form.formData).forEach((key) => {
        order[key] = form.formData[key].value;
      });
      return { ...order, deliveryId: form.deliveryId };
    });
  });

  const getDeliveryFormsCompleted = (deliveryIds: string[]) =>
    computed<boolean>(() => {
      // None of the deliveryForms included in the order has errors
      const formsIncluded = deliveryForms.value?.filter((form: { deliveryId: string }) =>
        deliveryIds.includes(form.deliveryId),
      );
      if (!formsIncluded.length) return false;
      const deliveryFormsCompleted = formsIncluded?.every((form) => !helpers.deliveryFormHasErrors(form));
      return deliveryFormsCompleted;
    });

  const getRequestedDeliveryDateForDeliveryId = (deliveryId = DELIVERY_ID) =>
    computed<string>(() => {
      const requestedDeliveryDate = getDeliveryFormById(deliveryId)?.value?.formData.requestedDeliveryDate?.value;

      if (requestedDeliveryDate && getAllActiveDeliveryIdsInOrder.value.includes(deliveryId as DeliveryId)) {
        if (requestedDeliveryDate instanceof Date) {
          return requestedDeliveryDate.toISOString();
        } else {
          return new Date(requestedDeliveryDate as string).toISOString();
        }
      }
      return new Date().toISOString();
    });

  return {
    selectedLocations,
    sendOrderReceipt,
    activeDeliveryIdsInOrder,
    rawLocations,
    deliveryOptions,
    setDeliveryOptions,
    setSelectedDeliveryId,
    setSelectedLocation,
    setNewDeliveryForm,
    setDeliveryFormField,
    setZipCode,
    setSendOrderReceipt,
    setDeliveryDays,
    deliveryDays,
    clearState,
    setRawLocations,
    setPreSelectedLocations,
    setActiveDeliveryIdsInOrder,
    setZipCodeFromAddress,
    activateDeliveryIdInOrder,
    activateNewDeliveryIdInOrder,
    copyDeliveryFormToExistingForms,
    setDeliveryAddressForDeliveryId,
    lateDeliveryExpressIsActive,
    selectedDeliveryId,
    getZipCode,
    getDeliveryNameFromId,
    getErpDeliveryMethodById,
    getOrderTypeForDeliveryId,
    getDeliveryFormById,
    getDeliveryOptionById,
    getAvailableDeliveryOptions,
    getAvailableDeliveryIds,
    getAllActiveDeliveryIdsInOrder,
    getStockLocationsList,
    getSelectedDeliveryId,
    getDeliveryFormsRequestData,
    getDeliveryAddress,
    getExpressDeliveryAddress,
    deliveryAddresses,
    getStockInput,
    getCurrentStockLocation,
    getCurrentStockLocations,
    getDeliveryFormsCompleted,
    getRequestedDeliveryDateForDeliveryId,
    zipCodes,
    deliveryForms,
    clearAddress,
  };
});
